import { useCallback, useEffect, useState } from 'react';
import { deleteFaq, getFaqs, upsertFaq } from '../services/faqService';

const initialForm = {
  question: '',
  answer: '',
  isActive: true,
};

const editIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);

const trashIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

function FaqFormModal({ open, faq, onClose, onSaved }) {
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isEditing = Boolean(faq?.id);

  useEffect(() => {
    if (!open) return;
    setForm(
      faq
        ? {
            question: faq.question || '',
            answer: faq.answer || '',
            isActive: faq.isActive !== false,
          }
        : initialForm
    );
    setSaving(false);
    setError('');
  }, [open, faq]);

  if (!open) return null;

  function updateField(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();

    const question = form.question.trim();
    const answer = form.answer.trim();

    if (!question) {
      setError('Question is required.');
      return;
    }
    if (!answer) {
      setError('Answer is required.');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const payload = {
        question,
        answer,
        isActive: form.isActive,
      };

      if (isEditing) {
        payload.id = faq.id;
      }

      await upsertFaq(payload);
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err.message || (isEditing ? 'Failed to update FAQ.' : 'Failed to add FAQ.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="add-offer-modal faq-form-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="faq-form-title"
      >
        <div className="add-offer-modal-header">
          <div>
            <h2 id="faq-form-title">{isEditing ? 'Edit FAQ' : 'Add FAQ'}</h2>
            <p>{isEditing ? 'Update this frequently asked question.' : 'Create a new frequently asked question.'}</p>
          </div>
          <button type="button" className="add-restaurant-close" onClick={onClose} aria-label="Close">
            &#x2715;
          </button>
        </div>

        <form className="add-offer-form" onSubmit={handleSubmit}>
          <label className="add-restaurant-field">
            <span>Question</span>
            <input
              type="text"
              placeholder="e.g. How do I buy petals?"
              value={form.question}
              onChange={(e) => updateField('question', e.target.value)}
              maxLength={300}
              required
            />
          </label>

          <label className="add-restaurant-field">
            <span>Answer</span>
            <textarea
              placeholder="Write the answer..."
              value={form.answer}
              onChange={(e) => updateField('answer', e.target.value)}
              rows={5}
              maxLength={2000}
              required
            />
          </label>

          <div className="add-restaurant-field">
            <span>Status</span>
            <div className="add-offer-status-toggle" role="group" aria-label="FAQ status">
              <button
                type="button"
                className={`add-offer-status-btn ${form.isActive ? 'is-active' : ''}`}
                onClick={() => updateField('isActive', true)}
              >
                Active
              </button>
              <button
                type="button"
                className={`add-offer-status-btn ${!form.isActive ? 'is-active' : ''}`}
                onClick={() => updateField('isActive', false)}
              >
                Inactive
              </button>
            </div>
          </div>

          {error && <div className="add-restaurant-error">{error}</div>}

          <div className="add-restaurant-footer">
            <button type="submit" className="add-restaurant-save" disabled={saving}>
              {saving ? 'Saving...' : isEditing ? 'Update FAQ' : 'Save FAQ'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Faqs() {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const loadFaqs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getFaqs();
      setFaqs(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(err.message || 'Failed to fetch FAQs.');
      setFaqs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFaqs();
  }, [loadFaqs]);

  function openAdd() {
    setEditingFaq(null);
    setShowForm(true);
  }

  function openEdit(faq) {
    setEditingFaq(faq);
    setShowForm(true);
  }

  function closeForm() {
    setShowForm(false);
    setEditingFaq(null);
  }

  function openDelete(faq) {
    setDeleteTarget(faq);
    setDeleteError('');
  }

  function closeDelete() {
    if (deleting) return;
    setDeleteTarget(null);
    setDeleteError('');
  }

  async function confirmDelete() {
    if (!deleteTarget?.id) return;
    setDeleting(true);
    setDeleteError('');
    try {
      await deleteFaq(deleteTarget.id);
      setDeleteTarget(null);
      await loadFaqs();
    } catch (err) {
      setDeleteError(err.message || 'Failed to delete FAQ.');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="faq-page main-bg-top">
      <div className="users-header faq-header">
        <button type="button" className="offers-add-btn" onClick={openAdd}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Add FAQ
        </button>
      </div>

      {loading ? (
        <div className="faq-empty">Loading FAQs...</div>
      ) : error ? (
        <div className="faq-empty">{error}</div>
      ) : faqs.length === 0 ? (
        <div className="faq-empty">No FAQs yet. Click “Add FAQ” to create one.</div>
      ) : (
        <div className="faq-list">
          {faqs.map((faq, index) => {
            const isExpanded = expandedId === faq.id;
            return (
              <article key={faq.id} className={`faq-card ${isExpanded ? 'is-expanded' : ''}`}>
                <div className="faq-card-header">
                  <button
                    type="button"
                    className="faq-card-toggle"
                    onClick={() => setExpandedId(isExpanded ? null : faq.id)}
                    aria-expanded={isExpanded}
                  >
                    <span className="faq-card-index">{String(index + 1).padStart(2, '0')}</span>
                    <span className="faq-card-question">{faq.question || 'Untitled question'}</span>
                  </button>

                  <div className="faq-card-actions">
                    <button
                      type="button"
                      className="faq-action-btn"
                      onClick={() => openEdit(faq)}
                      title="Edit"
                    >
                      {editIcon}
                      <span>Edit</span>
                    </button>
                    <button
                      type="button"
                      className="faq-action-btn faq-action-delete"
                      onClick={() => openDelete(faq)}
                      title="Delete"
                    >
                      {trashIcon}
                      <span>Delete</span>
                    </button>
                    <button
                      type="button"
                      className={`faq-card-chevron ${isExpanded ? 'open' : ''}`}
                      onClick={() => setExpandedId(isExpanded ? null : faq.id)}
                      aria-label={isExpanded ? 'Collapse answer' : 'Expand answer'}
                      aria-expanded={isExpanded}
                    >
                      <img alt="" className="menu-icon-img" src={`${process.env.PUBLIC_URL}/images/arrow.svg`} />
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="faq-card-body">
                    <p className="faq-card-answer">{faq.answer || '—'}</p>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}

      <FaqFormModal open={showForm} faq={editingFaq} onClose={closeForm} onSaved={loadFaqs} />

      {deleteTarget && (
        <div className="modal-overlay" onClick={closeDelete}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <h3 className="modal-title">Delete FAQ?</h3>
            <p className="modal-message">
              Are you sure you want to delete
              <br />
              <strong>{deleteTarget.question || 'this FAQ'}</strong>?
              <br />
              This action cannot be undone.
            </p>
            {deleteError && (
              <div className="add-restaurant-error" style={{ marginBottom: 16, textAlign: 'left' }}>
                {deleteError}
              </div>
            )}
            <div className="modal-actions">
              <button
                type="button"
                className="modal-btn modal-btn-cancel"
                onClick={closeDelete}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="modal-btn modal-btn-confirm"
                onClick={confirmDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
